import { apiUrl } from '@/api/client';
import { CHAT_MAX_BYTES_PER_FILE } from '@/lib/chatAttachments';
import { PWA_JWT_STORAGE_KEY } from '@/auth/storageKeys';

const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

export type ResourceImageRef = {
  id: string;
  mimeType: string;
  originalName: string;
  sortOrder: number;
};

export type ResourcePhotoGroup = {
  id: string;
  name: string;
  images: ResourceImageRef[];
};

export const EMPTY_RESOURCE_PHOTO_GROUPS: ResourcePhotoGroup[] = [];

function imageExt(mimeType: string): string {
  if (mimeType === 'image/png') return '.png';
  if (mimeType === 'image/webp') return '.webp';
  if (mimeType === 'image/gif') return '.gif';
  return '.jpg';
}

/** Recursos del agente que tienen fotos, uno por bloque. */
export function resourcePhotoGroups(resources: unknown): ResourcePhotoGroup[] {
  if (!Array.isArray(resources)) return [];
  const groups: ResourcePhotoGroup[] = [];
  for (const raw of resources) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    if (typeof row.id !== 'string' || typeof row.name !== 'string' || !row.name.trim()) continue;
    const media = Array.isArray(row.mediaAttachments) ? row.mediaAttachments : [];
    const images: ResourceImageRef[] = [];
    media.forEach((item, index) => {
      if (!item || typeof item !== 'object') return;
      const mediaRow = item as Record<string, unknown>;
      if (mediaRow.kind !== 'image' || typeof mediaRow.id !== 'string') return;
      const mimeType = typeof mediaRow.mimeType === 'string' ? mediaRow.mimeType : '';
      if (!IMAGE_MIMES.has(mimeType)) return;
      const sortOrder =
        typeof mediaRow.sortOrder === 'number' && Number.isFinite(mediaRow.sortOrder)
          ? mediaRow.sortOrder
          : index;
      images.push({
        id: mediaRow.id,
        mimeType,
        originalName: typeof mediaRow.originalName === 'string' ? mediaRow.originalName : '',
        sortOrder,
      });
    });
    images.sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
    if (images.length > 0) {
      groups.push({ id: row.id, name: row.name.trim(), images });
    }
  }
  return groups.sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export function resourceImageFileName(resourceName: string, image: ResourceImageRef): string {
  const house = resourceName.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'recurso';
  const raw = image.originalName.replace(/[\\/:*?"<>|]+/g, ' ').trim();
  const stem = (raw.replace(/\.[a-z0-9]{2,5}$/i, '') || 'foto').slice(0, 60);
  return `${house} - ${stem}${imageExt(image.mimeType)}`.slice(0, 120);
}

export function resourceMediaUrl(agentId: string, resourceId: string, attachmentId: string): string {
  return apiUrl(
    `/api/agents/${encodeURIComponent(agentId)}/calendar/resources/${encodeURIComponent(resourceId)}/media/${encodeURIComponent(attachmentId)}`
  );
}

export async function fetchResourceMediaBlob(
  agentId: string,
  resourceId: string,
  attachmentId: string,
  token: string | null
): Promise<Blob> {
  const auth = token ?? localStorage.getItem(PWA_JWT_STORAGE_KEY);
  const response = await fetch(resourceMediaUrl(agentId, resourceId, attachmentId), {
    cache: 'no-store',
    headers: auth ? { Authorization: `Bearer ${auth}` } : {},
  });
  if (!response.ok) throw new Error('No se pudo cargar la foto');
  const blob = await response.blob();
  if (!blob.size) throw new Error('La foto está vacía');
  return blob;
}

async function shrinkChatImage(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  try {
    const maxEdge = 1600;
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('La foto es demasiado grande para el chat');
    ctx.drawImage(bitmap, 0, 0, width, height);
    let quality = 0.86;
    let blob: Blob | null = null;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((result) => resolve(result), 'image/jpeg', quality);
      });
      if (blob && blob.size <= CHAT_MAX_BYTES_PER_FILE) break;
      quality -= 0.12;
    }
    if (!blob || blob.size > CHAT_MAX_BYTES_PER_FILE) {
      throw new Error('La foto es demasiado grande para el chat');
    }
    const name = file.name.replace(/\.[a-z0-9]{2,5}$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } finally {
    bitmap.close();
  }
}

/** Deja la foto dentro del tope del chat. */
export async function fitChatImageFile(file: File): Promise<File> {
  if (file.size <= CHAT_MAX_BYTES_PER_FILE) return file;
  return shrinkChatImage(file);
}

export async function loadResourceChatImage(args: {
  agentId: string;
  token: string | null;
  group: ResourcePhotoGroup;
  image: ResourceImageRef;
}): Promise<File> {
  const blob = await fetchResourceMediaBlob(args.agentId, args.group.id, args.image.id, args.token);
  const type = args.image.mimeType || blob.type || 'image/jpeg';
  const file = new File([blob], resourceImageFileName(args.group.name, args.image), { type });
  return fitChatImageFile(file);
}
